import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";

import {
  getExhibitAttemptStats,
  getExhibitSessionStats,
} from "../../api/aiExhibitAnalyticsClient";
import { getRealOrAIProductionRounds } from "../../api/aiExhibitContentClient";

const ROA = [
  { id: "photo", label: "Photo" },
  { id: "chat", label: "Chat" },
  { id: "video", label: "Short video" },
  { id: "news", label: "News / Trust" },
  { id: "voice", label: "Voice" },
  { id: "outrage", label: "Manipulation" },
];

function formatDuration(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n)) return "—";
  if (n < 60) return `${Math.round(n)} sec`;
  const minutes = Math.floor(n / 60);
  const rest = Math.round(n % 60);
  return rest ? `${minutes}m ${rest}s` : `${minutes} min`;
}

export default function ScoreboardScreen({
  tier = "hs",
  language = "en",
  onBack = () => {},
  contentVersion = "v2_bilingual_rc1",
  kioskId = null,
  studentEmail = null,
}) {
  const [roaStats, setRoaStats] = useState({});
  const [sessionStats, setSessionStats] = useState(null);
  const [error, setError] = useState("");
  const [productionVersions, setProductionVersions] = useState({});

  useEffect(() => {
    let active = true;

    const refresh = async () => {
      try {
        const productionRounds = await getRealOrAIProductionRounds();
        const versionMap = Object.fromEntries(
          productionRounds.map((round) => [round.id, round.contentVersion])
        );
        if (active) setProductionVersions(versionMap);

        const [attemptRows, sessions] = await Promise.all([
          Promise.all(
            ROA.map(async (round) => {
              const stats = await getExhibitAttemptStats({
                stationId: "real-or-ai",
                itemId: round.id,
                contentVersion: language === "es"
                  ? `${versionMap[round.id] || contentVersion}_es`
                  : versionMap[round.id] || contentVersion,
              });
              return [round.id, stats];
            })
          ),
          getExhibitSessionStats({ days: 7, kioskId, studentEmail: kioskId ? null : studentEmail }),
        ]);

        if (!active) return;
        setRoaStats(Object.fromEntries(attemptRows));
        setSessionStats(sessions);
        setError("");
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Live analytics are temporarily unavailable.");
      }
    };

    refresh();
    const timer = setInterval(refresh, 10000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [contentVersion, kioskId, studentEmail, language]);

  return (
    <View style={s.container}>
      <View style={s.top}>
        <Pressable onPress={onBack}><Text style={s.topText}>← Menu</Text></Pressable>
        <Text style={s.topText}>Live exhibit statistics</Text>
      </View>

      <View style={s.content}>
        <Text style={s.title}>
          {tier === "el" ? "How is everyone doing?" : "How is the exhibit doing?"}
        </Text>
        <Text style={s.subtitle}>
          These are live Saras records. Every submitted guess counts as an attempt; session statistics are tracked separately.
        </Text>

        {!!error && <Text style={s.error}>{error}</Text>}

        <View style={s.grid}>
          <Panel wide title="🧭 MEDIA CHALLENGES · RECORDED ATTEMPTS">
            {ROA.map((round) => {
              const stats = roaStats[round.id];
              const n = Number(stats?.scored_attempts || 0);
              return (
                <AttemptRow
                  key={round.id}
                  label={round.label}
                  attempts={n}
                  pctIncorrect={n >= 5 ? stats?.pct_incorrect : null}
                />
              );
            })}
          </Panel>

          <Panel title="👥 KIOSK SESSIONS · LAST 7 DAYS">
            <Big
              value={Number(sessionStats?.sessions || 0).toLocaleString(language === "es" ? "es-ES" : "en-US")}
              caption="sessions"
            />
            <Metric label="Average duration" value={formatDuration(sessionStats?.avg_duration_seconds)} />
            <Metric label="Median duration" value={formatDuration(sessionStats?.median_duration_seconds)} />
          </Panel>

          <Panel title="🧪 ACTIVITY · LAST 7 DAYS">
            <Big
              value={Number(sessionStats?.attempts || 0).toLocaleString(language === "es" ? "es-ES" : "en-US")}
              caption="attempts across tracked games"
            />
            <Metric label="Recorded events" value={Number(sessionStats?.events || 0).toLocaleString(language === "es" ? "es-ES" : "en-US")} />
          </Panel>
        </View>

        <Text style={s.note}>
          Percentages appear after 5 scored attempts for that exact content version · Photo, Chat, Video, News/Trust, Voice, and Manipulation use production content versions · no seeded or placeholder percentages
        </Text>
      </View>
    </View>
  );
}

function Panel({ title, wide = false, children }) {
  return (
    <View style={[s.panel, wide && s.panelWide]}>
      <Text style={s.panelTitle}>{title}</Text>
      {children}
    </View>
  );
}

function Big({ value, caption }) {
  return (
    <View>
      <Text style={s.big}>{value}</Text>
      <Text style={s.bigCaption}>{caption}</Text>
    </View>
  );
}

function Metric({ label, value }) {
  return (
    <View style={s.metricRow}>
      <Text style={s.metricLabel}>{label}</Text>
      <Text style={s.metricValue}>{value}</Text>
    </View>
  );
}

function AttemptRow({ label, attempts, pctIncorrect }) {
  const havePercent = Number.isFinite(Number(pctIncorrect));
  const safe = havePercent ? Math.max(0, Math.min(100, Number(pctIncorrect))) : 0;

  return (
    <View style={s.barBlock}>
      <View style={s.barHeader}>
        <Text style={s.barLabel}>{label}</Text>
        <Text style={s.barValue}>
          {havePercent ? `${safe}% wrong · ${attempts} attempts` : `${attempts} attempts`}
        </Text>
      </View>
      <View style={s.track}>
        {havePercent ? <View style={[s.fill, { width: `${safe}%` }]} /> : null}
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  top: { height: 72, paddingHorizontal: 48, flexDirection: "row", gap: 26, alignItems: "center" },
  topText: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  content: { paddingHorizontal: 100 },
  title: { marginTop: 22, color: "#F4F8FC", fontSize: 58, fontWeight: "900" },
  subtitle: { marginTop: 8, color: "#9FB2C6", fontSize: 21, lineHeight: 31, maxWidth: 1450 },
  error: { marginTop: 12, color: "#FBBF24", fontSize: 18, fontWeight: "800" },
  grid: { marginTop: 24, flexDirection: "row", flexWrap: "wrap", gap: 18 },
  panel: { width: 520, minHeight: 280, padding: 24, borderRadius: 20, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626" },
  panelWide: { width: 780, minHeight: 578 },
  panelTitle: { color: "#8FA5BC", fontSize: 15, fontWeight: "900", letterSpacing: 1.2 },
  barBlock: { marginTop: 18 },
  barHeader: { flexDirection: "row", justifyContent: "space-between", gap: 14 },
  barLabel: { color: "#F4F8FC", fontSize: 18, fontWeight: "800", flex: 1 },
  barValue: { color: "#4FD1FF", fontSize: 17, fontWeight: "900" },
  track: { marginTop: 7, height: 14, borderRadius: 999, overflow: "hidden", backgroundColor: "#132037" },
  fill: { height: "100%", backgroundColor: "#4FD1FF" },
  big: { marginTop: 28, color: "#5ADC9B", fontSize: 60, fontWeight: "900" },
  bigCaption: { marginTop: 8, color: "#9FB2C6", fontSize: 20, lineHeight: 28 },
  metricRow: { marginTop: 24, flexDirection: "row", justifyContent: "space-between", gap: 20 },
  metricLabel: { color: "#9FB2C6", fontSize: 18 },
  metricValue: { color: "#F4F8FC", fontSize: 18, fontWeight: "900" },
  note: { marginTop: 18, color: "#5F7690", fontSize: 15 },
});
